import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Stack,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import { Close, Check } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { useCreateSponsoredUserMutation } from "../../../redux/services/sponsorship.service";
import { useGetOrganizationsQuery } from "../../../redux/services/organization.service";
import { selectUser } from "../../../redux/slices/auth.slice";

AddBeneficiaryForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default function AddBeneficiaryForm({ open, onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const user = useSelector(selectUser);
  const roles = user?.roles?.map((role) => role?.name) || [];
  const isOrgAdmin = roles.includes("organization");
  const userOrgId = user?.organizationId || localStorage.getItem("organizationId") || "";

  const { data: organizations, isLoading: isOrgLoading } = useGetOrganizationsQuery();
  const [createSponsoredUser, { isLoading: isCreating }] = useCreateSponsoredUserMutation();

  const [formValues, setFormValues] = useState({
    name: "",
    contactNumber: "",
    email: "",
    organizationId: "",
  });

  const [errors, setErrors] = useState({});
  const [activeSubscriptionError, setActiveSubscriptionError] = useState("");

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      // Set organization ID based on role
      let defaultOrgId = "";
      if (isOrgAdmin) {
        defaultOrgId = userOrgId;
      } else {
        // Default to the first organization in the list
        defaultOrgId = organizations?.data?.[0]?.id || "";
      }

      setFormValues({
        name: "",
        contactNumber: "",
        email: "",
        organizationId: defaultOrgId,
      });
      setErrors({});
      setActiveSubscriptionError("");
    }
  }, [open, isOrgAdmin, userOrgId, organizations]);

  const handleInputChange = (field) => (event) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // ---------------------------------------------------------------------------
  // Validation rules
  // ---------------------------------------------------------------------------
  // Name: 2–50 chars, letters (incl. accented) and spaces only — no numbers or special symbols.
  const NAME_REGEX = /^[a-zA-Z\u00C0-\u024F][a-zA-Z\u00C0-\u024F\s]{1,49}$/;
  // Email: RFC-5321 aligned (no consecutive dots, valid TLD 2+ chars), max 254.
  const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const validate = () => {
    const tempErrors = {};
    const name = formValues.name.trim();
    const email = formValues.email.trim();
    const phone = formValues.contactNumber.trim();

    // ── Name ────────────────────────────────────────────────────────────────
    if (!name) {
      tempErrors.name = "Full name is required";
    } else if (name.length < 2) {
      tempErrors.name = "Name must be at least 2 characters";
    } else if (name.length > 50) {
      tempErrors.name = "Name must not exceed 50 characters";
    } else if (!NAME_REGEX.test(name)) {
      tempErrors.name = "Name may only contain letters and spaces";
    }

    // ── Contact (at least one of email / phone required) ────────────────────
    if (!email && !phone) {
      tempErrors.email = "Email or Contact Number is required";
      tempErrors.contactNumber = "Email or Contact Number is required";
    } else {
      // Email checks
      if (email) {
        if (email.length > 254) {
          tempErrors.email = "Email must not exceed 254 characters";
        } else if (!EMAIL_REGEX.test(email)) {
          tempErrors.email = "Enter a valid email address (e.g. rahul@example.com)";
        }
      }
      // Phone checks
      if (phone) {
        if (!/^\d{10}$/.test(phone)) {
          tempErrors.contactNumber = "Contact number must be exactly 10 digits";
        } else if (/^(\d)\1{9}$/.test(phone)) {
          tempErrors.contactNumber = "Enter a valid contact number";
        }
      }
    }

    if (!formValues.organizationId) {
      tempErrors.organizationId = "Organization is required";
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      organizationId: formValues.organizationId,
    };
    if (formValues.name.trim()) payload.name = formValues.name;
    if (formValues.email.trim()) payload.email = formValues.email;
    if (formValues.contactNumber.trim()) payload.contactNumber = formValues.contactNumber;

    try {
      const response = await createSponsoredUser(payload);
      if (response?.data?.success) {
        enqueueSnackbar("Beneficiary registered successfully", { variant: "success" });
        onClose();
      } else {
        const errMsg = response?.error?.data?.message || "Failed to register beneficiary";
        setActiveSubscriptionError(errMsg);
      }
    } catch (error) {
      enqueueSnackbar("An error occurred during submission", { variant: "error" });
    }
  };

  const selectedOrgName = organizations?.data?.find(
    (org) => org.id === formValues.organizationId
  )?.name || "Voice of SAP";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" fontWeight="bold">
          Register New Beneficiary
        </Typography>
        <IconButton aria-label="close" onClick={onClose} sx={{ color: "grey.500" }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 4 }}>
        {activeSubscriptionError && (
          <Alert
            severity="warning"
            onClose={() => setActiveSubscriptionError("")}
            sx={{ mb: 2, justifyContent: "center", textAlign: "center" }}
          >
            {activeSubscriptionError}
          </Alert>
        )}
        <Stack spacing={3}>
          <TextField
            label="Full Name"
            value={formValues.name}
            onChange={handleInputChange("name")}
            error={!!errors.name}
            helperText={errors.name || "Letters and spaces only — 2 to 50 characters"}
            fullWidth
            inputProps={{ maxLength: 50 }}
          />
          <TextField
            label="Email Address"
            value={formValues.email}
            onChange={handleInputChange("email")}
            error={!!errors.email}
            helperText={errors.email || "e.g. rahul@example.com"}
            fullWidth
            inputProps={{ maxLength: 254 }}
          />
          <TextField
            label="Contact Number"
            value={formValues.contactNumber}
            onChange={handleInputChange("contactNumber")}
            error={!!errors.contactNumber}
            helperText={errors.contactNumber || "10-digit mobile number"}
            fullWidth
            placeholder="10-digit mobile number"
            inputProps={{ maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }}
          />

          {!isOrgAdmin && (
            <FormControl fullWidth error={!!errors.organizationId}>
              <InputLabel id="dialog-org-label">Target Organization</InputLabel>
              <Select
                labelId="dialog-org-label"
                id="dialog-org-select"
                value={formValues.organizationId}
                label="Target Organization"
                onChange={handleInputChange("organizationId")}
              >
                {isOrgLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} />
                  </MenuItem>
                ) : (
                  organizations?.data?.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.organizationId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.organizationId}
                </Typography>
              )}
            </FormControl>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} variant="outlined" color="inherit" disabled={isCreating}>
          Cancel
        </Button>
        <Button
          startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : <Check />}
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isCreating}
        >
          Register
        </Button>
      </DialogActions>
    </Dialog>
  );
}
